/** A field's location inside an options object, e.g. `["chunkingOptions", "prompt_cache", "enabled"]`. */
export type OptionPath = readonly string[];

type Bag = Record<string, unknown>;

const isBag = (value: unknown): value is Bag =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** An unset option: the runtime default applies. */
export const isUnset = (value: unknown) =>
  value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

export const getPath = (source: unknown, path: OptionPath): unknown =>
  path.reduce<unknown>((node, key) => (isBag(node) ? node[key] : undefined), source);

/**
 * Immutably set (or, for an unset value, delete) `path` in `source`. Parents
 * left empty by a delete are removed too, and the result is null when nothing
 * is left, which is how every options object means "all runtime defaults".
 */
export const setPath = <T extends object>(source: T | null | undefined, path: OptionPath, value: unknown): T | null => {
  const write = (node: unknown, depth: number): Bag | undefined => {
    const next: Bag = isBag(node) ? { ...node } : {};
    const key = path[depth];
    if (depth === path.length - 1) {
      if (isUnset(value)) delete next[key];
      else next[key] = value;
    } else {
      const child = write(next[key], depth + 1);
      if (child) next[key] = child;
      else delete next[key];
    }
    return Object.keys(next).length ? next : undefined;
  };
  return (write(source, 0) as T | undefined) ?? null;
};
